// KLUB-64 (#252) — canonical shape, defaults, ranges and normalizer for the
// `app.home` tenant setting.
//
// This file — not app.ts — owns the numbers. app.ts derives its Zod schema and
// its default from HOME_LIMITS / HOME_CONFIG_DEFAULTS below, so a range lives
// in exactly one place on this side of the wire. The Deno mirrors
// (_shared/tenant-settings-types.ts and write-tenant-setting/validators.ts)
// restate them as literals because edge functions cannot import this package;
// home-config.test.ts fails the moment the two drift.
//
// Why a normalizer at all, on top of the edge fn's Zod validation: the row is
// JSONB and Zod only guards the write path. A hand-written SQL row, an
// over-broad staff write, or a row written by an older schema all reach the
// reader unvalidated. Every consumer (admin section, mobile hook) runs this
// first, so an invalid leaf degrades to its own default instead of taking the
// whole config — or the screen — down with it (R36).

import { HOME_ALL_MODE_CAP, HOME_PERIOD_MODE_CAP } from "../constants";

/** How the home decides which upcoming activities to show.
 *  - `count`  — the next N activities
 *  - `period` — every activity starting within the next N days
 *  - `all`    — every upcoming activity, bounded only by HOME_ALL_MODE_CAP */
export const HOME_EVENTS_MODES = ["count", "period", "all"] as const;
export type HomeEventsMode = (typeof HOME_EVENTS_MODES)[number];

export interface HomeConfig {
  /** Legacy leaf. The activities section title is the activity noun, and the
   *  app ignores this value. The admin always writes null so an old title is
   *  cleared on the next save. The key stays so an old row still parses.
   *  Never an empty string: `min(1)` and "" contradict each other. */
  events_title: string | null;
  /** Title of the app's feed section — the home section, the full screen and
   *  the post detail header all read this one value (KLUB-131 / #341).
   *
   *  `null` means "use the feed noun's plural". A stored string still wins,
   *  so a club can name the section something other than that plural.
   *
   *  Never an empty string, for the same reason as `events_title`. */
  feed_title: string | null;
  events_mode: HomeEventsMode;
  /** Only read when `events_mode === 'count'`. */
  events_count: number;
  /** Only read when `events_mode === 'period'`. */
  events_period_days: number;
  /** Rows in the home's feed section. */
  feed_limit: number;
  /** Items the hero carousel shows, across both entity types. */
  hero_limit: number;
  /** Seconds each hero slide holds before rotating. */
  rotate_seconds: number;
}

/** Generic defaults — deliberately equal to the behaviour the app had while the
 *  config lived in `system_settings` and every non-staff reader got zero rows.
 *  A tenant that never customises anything must not see the home change. */
export const HOME_CONFIG_DEFAULTS: HomeConfig = {
  events_title: null,
  feed_title: null,
  events_mode: "count",
  events_count: 5,
  events_period_days: 7,
  feed_limit: 5,
  hero_limit: 3,
  rotate_seconds: 6,
};

/** Accepted ranges for the numeric leaves. app.ts builds the Zod schema from
 *  these; the normalizer clamps to them. */
export const HOME_LIMITS = {
  events_count: { min: 1, max: HOME_ALL_MODE_CAP },
  events_period_days: { min: 1, max: 90 },
  feed_limit: { min: 1, max: 50 },
  hero_limit: { min: 1, max: 10 },
  rotate_seconds: { min: 3, max: 30 },
} as const;

export const HOME_TITLE_MAX = 60;

/** Default feed section title. Same word as `vocabulary.defaults.feed.plural`.
 *  A null `feed_title` resolves to the published plural; this literal is that
 *  word before a publication arrives, and the first frame of the home hook. */
export const HOME_FEED_TITLE_FALLBACK = "Novidades";

type NumericKey = keyof typeof HOME_LIMITS;
type TitleKey = "events_title" | "feed_title";

/** What the normalizer had to repair. Consumers that care (the mobile hook)
 *  log these; consumers that don't ignore them. Never thrown — a bad leaf must
 *  not cost the caller the whole config. */
export type HomeConfigIssue = { field: keyof HomeConfig; reason: string };

function normalizeNumber(
  raw: unknown,
  key: NumericKey,
  issues: HomeConfigIssue[],
): number {
  const { min, max } = HOME_LIMITS[key];
  if (typeof raw !== "number" || !Number.isFinite(raw)) {
    if (raw !== undefined) issues.push({ field: key, reason: `not a number: ${JSON.stringify(raw)}` });
    return HOME_CONFIG_DEFAULTS[key];
  }
  const int = Math.trunc(raw);
  if (int < min) {
    issues.push({ field: key, reason: `${int} below ${min}, clamped` });
    return min;
  }
  if (int > max) {
    issues.push({ field: key, reason: `${int} above ${max}, clamped` });
    return max;
  }
  return int;
}

/** Trim, cap at HOME_TITLE_MAX, and read "" as `null`.
 *
 *  Both title leaves share the coercion; what `null` RESOLVES TO differs per
 *  leaf and is decided by the consumer, not here — see HomeConfig. */
function normalizeTitle(
  raw: unknown,
  key: TitleKey,
  issues: HomeConfigIssue[],
): string | null {
  if (typeof raw !== "string") {
    if (raw != null) issues.push({ field: key, reason: "not a string" });
    return HOME_CONFIG_DEFAULTS[key];
  }
  // "" is not a legal stored value — it is how a cleared input arrives before
  // the admin section maps it back to null. Same meaning as null.
  const trimmed = raw.trim();
  return trimmed.length === 0 ? null : trimmed.slice(0, HOME_TITLE_MAX);
}

/**
 * Coerce an untrusted JSONB `app.home` value into a safe HomeConfig, merging
 * LEAF BY LEAF over HOME_CONFIG_DEFAULTS.
 *
 * Leaf-by-leaf matters twice. On the read side, a row written by an older
 * schema is missing the newer leaves and a shallow `{...DEFAULTS, ...raw}`
 * would still work — but a leaf with the wrong *type* would survive and reach
 * the UI. On the write side, the admin's settings provider merges by SECTION,
 * so a partial `home` object would replace the whole object; normalizing on
 * read is what keeps that from erasing leaves the writer never touched.
 *
 * Type errors fall back to that leaf's default; out-of-range numbers are
 * clamped to the range rather than discarded, because a tenant that asked for
 * 99 hero slots wants "as many as allowed", not "back to 3".
 */
export function normalizeHomeConfigWithIssues(raw: unknown): {
  config: HomeConfig;
  issues: HomeConfigIssue[];
} {
  const issues: HomeConfigIssue[] = [];
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    if (raw != null) issues.push({ field: "events_mode", reason: "value is not an object" });
    return { config: { ...HOME_CONFIG_DEFAULTS }, issues };
  }
  const o = raw as Record<string, unknown>;

  const events_title = normalizeTitle(o.events_title, "events_title", issues);
  const feed_title = normalizeTitle(o.feed_title, "feed_title", issues);

  let events_mode: HomeEventsMode = HOME_CONFIG_DEFAULTS.events_mode;
  if (typeof o.events_mode === "string" && (HOME_EVENTS_MODES as readonly string[]).includes(o.events_mode)) {
    events_mode = o.events_mode as HomeEventsMode;
  } else if (o.events_mode !== undefined) {
    issues.push({ field: "events_mode", reason: `unknown mode: ${JSON.stringify(o.events_mode)}` });
  }

  return {
    config: {
      // KEY ORDER IS LOAD-BEARING. write-tenant-setting decides whether to
      // DELETE the row by `JSON.stringify(a) === JSON.stringify(b)`, so this
      // object and HOME_CONFIG_DEFAULTS must declare the leaves in the same
      // order or the delete-when-default path silently stops matching (KTD4).
      events_title,
      feed_title,
      events_mode,
      events_count: normalizeNumber(o.events_count, "events_count", issues),
      events_period_days: normalizeNumber(o.events_period_days, "events_period_days", issues),
      feed_limit: normalizeNumber(o.feed_limit, "feed_limit", issues),
      hero_limit: normalizeNumber(o.hero_limit, "hero_limit", issues),
      rotate_seconds: normalizeNumber(o.rotate_seconds, "rotate_seconds", issues),
    },
    issues,
  };
}

/** The canonical normalizer. Pure; see normalizeHomeConfigWithIssues for the
 *  variant that also reports what it repaired. */
export function normalizeHomeConfig(raw: unknown): HomeConfig {
  return normalizeHomeConfigWithIssues(raw).config;
}

/**
 * How many upcoming activities the carousel reads, for the configured mode.
 * The two modes without an explicit count resolve to their own sanity ceiling
 * (R6, R47) — neither is tenant-configurable, and neither may be left
 * unbounded.
 */
export function resolveEventsLimit(config: HomeConfig): number {
  switch (config.events_mode) {
    case "all":
      return HOME_ALL_MODE_CAP;
    case "period":
      return HOME_PERIOD_MODE_CAP;
    default:
      return config.events_count;
  }
}
